/**
 * Persistent selection highlight for ProseMirror surfaces.
 *
 * There is exactly one document selection, so the moment an AI surface focuses
 * its own input the editor's native highlight is gone — the user is left asking
 * "what is this prompt even acting on?". ProseMirror's *state* selection
 * survives the blur, so we paint that range ourselves with an inline decoration
 * for as long as the AI surface is open.
 *
 * The plugin is added lazily on first use (same `reconfigure` pattern as
 * DocsApp's ghost-text plugin) so editors don't have to thread it through their
 * plugin lists to opt in — a one-line `setSelectionHighlight` in their
 * useAISelectionSource call is the whole adoption cost.
 */

import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import type { EditorView } from 'prosemirror-view';
import './aiSelectionHighlight.scss';

export const AI_SELECTION_HIGHLIGHT_CLASS = 'ai-selection-highlight';

const aiSelectionHighlightKey = new PluginKey<DecorationSet>('aiSelectionHighlight');

function aiSelectionHighlightPlugin(): Plugin<DecorationSet> {
  return new Plugin<DecorationSet>({
    key: aiSelectionHighlightKey,
    state: {
      init: () => DecorationSet.empty,
      apply(tr, current) {
        const meta = tr.getMeta(aiSelectionHighlightKey) as { active: boolean } | undefined;
        if (!meta) {
          // No instruction — carry the existing highlight through the edit so a
          // collaborator typing above the selection doesn't shift it off target.
          return current.map(tr.mapping, tr.doc);
        }
        if (!meta.active) return DecorationSet.empty;

        const { from, to } = tr.selection;
        if (from === to) return DecorationSet.empty;
        return DecorationSet.create(tr.doc, [
          Decoration.inline(from, to, { class: AI_SELECTION_HIGHLIGHT_CLASS }),
        ]);
      },
    },
    props: {
      decorations: (state) => aiSelectionHighlightKey.getState(state),
    },
  });
}

/**
 * Show or hide the persistent highlight over the view's current selection.
 * Safe to call on a null or destroyed view, and safe to call repeatedly.
 */
export function setProseMirrorAISelectionHighlight(
  view: EditorView | null | undefined,
  active: boolean,
): void {
  if (!view || !(view as any).docView) return;

  if (!aiSelectionHighlightKey.getState(view.state)) {
    // Nothing to clear if the plugin was never installed.
    if (!active) return;
    view.updateState(
      view.state.reconfigure({ plugins: [...view.state.plugins, aiSelectionHighlightPlugin()] }),
    );
  }

  view.dispatch(view.state.tr.setMeta(aiSelectionHighlightKey, { active }));
}
